import { CTA_CLICK_SMS_OPT_IN } from './constants';
import { getFacebookCookies } from './general/getFacebookCookies';
import { getOptimizelyData } from './general/getOptimizelyData';
import { goToSelfSchedule, goToSelfScheduleOnPage } from './selfSchedule';

// Same-origin sessionStorage handoff contract with the self-schedule app
// (WEBDEV-4043). Bump the version if the shape changes.
const HANDOFF_STORAGE_KEY = 'milan.ssa.handoff.v1';
const HANDOFF_VERSION = 1;

// Contract for whoever builds the SSA-side reader of this payload — three
// responsibilities surfaced during WEBDEV-4039's review that corporate can't
// satisfy on its own, since none of them are checkable until that reader
// exists:
//
// 1. Validate `clientInfo.phone` with the SSA's own isValidPhone()/
//    normalizePhone() (packages/self-schedule/src/lib/validation/phone.ts —
//    functionally identical to normalizePhoneForHandoff() below) before
//    skipping Step 1. A blank/invalid phone here means the input didn't
//    cleanly resolve to a US 10-digit number — render Step 1 pre-filled with
//    name/email rather than skip to Step 2 with an unreachable lead.
//
// 2. Read `startedAt` off HandoffPayload and use it as the start of the span
//    when emitting `self_schedule_timing` on successful lead creation, gated
//    on intent === 'schedule' (mirrors this form's own gate on firing
//    self_schedule_start — see useSelfScheduleTimer on the corporate side).
//
// 3. Clear sessionStorage[HANDOFF_STORAGE_KEY] immediately after consuming
//    it. Corporate overwrites this key on every submit but never clears it,
//    so an unconsumed payload persists for the life of the tab — without
//    this, a user who returns to /specials and later reaches /schedule by
//    another route (header link, bookmark) would have the SSA pick up a
//    stale handoff and skip Step 2 with old data.

export const INTENT = {
    schedule: 'schedule',
    question: 'question',
} as const;
export type Intent = (typeof INTENT)[keyof typeof INTENT];

interface ContactFormFields {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
}

export interface OnPageClientInfo {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    smsOptIn: boolean;
}

interface HandoffAttribution {
    cloudamp__data__c: string;
    fbp: string | null;
    fbc: string | null;
    experiment_exposures: string | null;
    experiment_user_ID: string | null;
}

interface HandoffPayload {
    v: number;
    intent: Intent;
    clientInfo: OnPageClientInfo;
    attribution: HandoffAttribution;
    // When the schedule-intent submit began (captured before reCAPTCHA), for
    // analytics parity with ConsultationForm's self_schedule_start/timing
    // events (see useSelfScheduleTimer). Kept separate from `ts` (written
    // after reCAPTCHA) since the gap between the two is real latency worth
    // preserving. The SSA side isn't reading this yet — see WEBDEV-4118/
    // WEBDEV-4043 — but it's included so that build has it available.
    startedAt: number | null;
    ts: number;
}

// SSA's own phone validation (normalizePhone) requires exactly 10 digits and
// rejects an 11-digit "+1"-prefixed number — so a raw "+1 999-999-9999" from
// PhoneInput would fail SSA's guard rail and silently fall back to a normal
// Step 1 start. Strip a leading US country code and format to match SSA's own
// display mask, "(XXX) XXX-XXXX" (see apps/self-schedule/utils/client.ts),
// so a Back/Edit inside the SSA shows the same formatting the user typed.
function normalizePhoneForHandoff(rawPhone: string): string {
    let digits = (rawPhone || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
    }
    if (digits.length !== 10) return '';
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

// Maps corporate's Formik field names to the SSA's ClientInfo shape.
export function buildOnPageClientInfo({ first_name, last_name, email, phone }: ContactFormFields): OnPageClientInfo {
    return {
        firstName: first_name,
        lastName: last_name,
        email,
        phone: normalizePhoneForHandoff(phone),
        // Clicking either CTA is the opt-in (WEBDEV-4493); the form has no checkbox.
        smsOptIn: CTA_CLICK_SMS_OPT_IN,
    };
}

// Writes the handoff payload and navigates to the SSA. No lead is created
// corporate-side here — that part of the ticket's load-time/handoff-performance
// concern still holds — but the caller's reCAPTCHA round trip (added after
// this was first written) does add real latency before this function runs.
//
// Attribution is included best-effort so the SSA has everything corporate
// captured, whether or not the SSA's current implementation reads it yet.
//
// intent defaults to 'schedule' if falsy — defensive against Enter-key
// implicit submission or any other path that reaches here without a button's
// onClick having set it, since an empty intent would make the SSA fall back
// to a normal Step 1 start instead of skipping to Step 2.
//
// sessionStorage/localStorage can throw (Safari private browsing, blocked
// storage, quota) — if that happens the handoff is lost, but the user should
// still end up in the SSA rather than stuck on a dead button; a normal
// Step 1 start (re-entering their info) is a better failure than nothing.
export function writeOnPageHandoff(
    clientInfo: OnPageClientInfo,
    intent: Intent | '',
    { cloudampData, startedAt }: { cloudampData?: string; startedAt?: number } = {}
): void {
    if (typeof window === 'undefined') return;

    // Scoped separately from the sessionStorage write below: localStorage can
    // throw independently of sessionStorage (e.g. some Safari/ITP and
    // embedded-webview configurations block one but not the other), and a
    // failure here is only a missing attribution field, not a reason to
    // discard an otherwise-deliverable handoff.
    let storedCloudampData = '';
    try {
        storedCloudampData = localStorage.getItem('cloudamp__data__c') || '';
    } catch {
        // Attribution best-effort — leave it blank rather than failing the handoff.
    }

    try {
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();

        const payload: HandoffPayload = {
            v: HANDOFF_VERSION,
            intent: intent || INTENT.schedule,
            clientInfo,
            attribution: {
                cloudamp__data__c: cloudampData || storedCloudampData,
                fbp,
                fbc,
                experiment_exposures,
                experiment_user_ID,
            },
            startedAt: startedAt ?? null,
            ts: Date.now(),
        };

        sessionStorage.setItem(HANDOFF_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
        console.warn('[selfScheduleHandoff] Storage write failed — falling back to a normal Step 1 start', error);
        goToSelfSchedule();
        return;
    }

    goToSelfScheduleOnPage();
}
